import { useController } from "react-hook-form";
import { RESIZE_VALUE, Textarea, useThemedStyletron } from "@bluecrew/blueprint-web";
import React, { useState } from "react";
import { styled } from "baseui";
import { FieldLabelRow } from "./FieldLabelRow";
import { FieldRowWrapper } from "./FieldRowWrapper";
import { errorMsg } from "./validationErrorMessages";
import { RelativeFlexFieldWrapper } from "./RelativeFlexFieldWrapper";
import { FieldContentExample } from "./FieldContentExample/FieldContentExample";
import { ExamplesTitleContainer, ExamplesToggleButton } from "./ExamplesToggleButton";
import { Spacer, SpacerType } from "../../components/Spacer";
import { DescriptionFieldProps } from "./types/propTypes/DescriptionField.types";
import { FieldTextLength } from "./FieldTextLength";

const DESCRIPTION_MIN_LENGTH = 10;
const DESCRIPTION_MAX_LENGTH = 5000;

const descriptionExamples = [
  {
    id: "2.1",
    text: "List the most important duties first (Crew Members will read this on their mobile phones).",
  },
  {
    id: "2.2",
    text: "To save space, don't repeat any requirements included in the General Requirements listed below.",
  },
  {
    id: "2.3",
    text:
      "Be detailed -- you can send home a Crew Member without pay if they cannot fulfill the requirements\n" +
      "listed here, which protects your business in the event of a bad Crew Member experience.",
  },
];

export const DescriptionField = ({
  control,
  trigger,
  disabled = false,
  editPositionScreen,
  examplesEnabled = false,
}: DescriptionFieldProps) => {
  const [examplesVisible, setExamplesVisible] = useState(false);
  const fieldName = "description";
  const validate = trigger.bind(null, fieldName);

  const {
    field: { onChange, value },
    meta: { invalid },
  } = useController({
    name: fieldName,
    control,
    rules: {
      required: true,
      minLength: {
        value: DESCRIPTION_MIN_LENGTH,
        message: errorMsg("min", DESCRIPTION_MIN_LENGTH),
      },
      // TODO: BW-303 - Remove this after Generic description will be separated
      //  from the customer supplied description
      ...(!editPositionScreen && {
        maxLength: {
          value: DESCRIPTION_MAX_LENGTH,
          message: errorMsg("max", DESCRIPTION_MAX_LENGTH),
        },
      }),
    },
  });

  const handleChange = (instructions: string) => {
    onChange(instructions);
  };

  const [, theme] = useThemedStyletron();
  const [fieldFocus, onFieldFocus] = useState(false);

  return (
    <RelativeFlexFieldWrapper data-pendo-key="DescriptionEditField">
      <FieldRowWrapper>
        <ExamplesTitleContainer>
          <FieldLabelRow>Position-specific requirements</FieldLabelRow>
          {examplesEnabled && (
            <ExamplesToggleButton onClick={() => setExamplesVisible(!examplesVisible)}>
              Suggestions
            </ExamplesToggleButton>
          )}
        </ExamplesTitleContainer>
        <Spacer $type={SpacerType.horizontal} $size={theme.sizing.scale600} />
        <InputWrapper>
          <Textarea
            value={value}
            error={invalid}
            onChange={(text) => handleChange(text)}
            onFocus={() => onFieldFocus(true)}
            onBlur={() => {
              onFieldFocus(false);
              validate();
            }}
            placeholder="Add additional details specific to the position."
            disabled={disabled}
            resize={RESIZE_VALUE.vertical}
            styles={{ minHeight: "200px" }}
          />
          <FieldTextLength
            focused={fieldFocus}
            error={invalid}
            minLength={DESCRIPTION_MIN_LENGTH}
            maxLength={DESCRIPTION_MAX_LENGTH}
            length={String(value).length}
          />
        </InputWrapper>

        <Spacer $type={SpacerType.horizontal} $size={theme.sizing.scale600} />
      </FieldRowWrapper>
      <FieldContentExample examples={descriptionExamples} examplesVisible={examplesVisible} />
    </RelativeFlexFieldWrapper>
  );
};

const InputWrapper = styled("div", () => ({ width: "502.82px" }));
